// The strip under the scrubber: one dot per point of interest at its start
// time, colored by kind, a faint range bar for points longer than a few
// seconds, idle stretches dimmed, and nearby dots clustered. Hover shows
// the card (with the supporting still); click seeks.
import { useMemo, useRef, useState } from "react";
import cn from "classnames";
import LeadFlagPopover, { PopoverItem } from "../../Investigations/LeadFlagPopover";
import { VideoPoint, formatClock, kindStyle } from "./types";

const CLUSTER_PX = 7;

interface Props {
  points: VideoPoint[];
  durationMs: number;
  currentMs: number;
  idle?: { start_ms: number; end_ms: number }[];
  activeId?: string | null; // rail hover → enlarged dot
  onHover?: (ids: string[]) => void;
  onSeek: (ms: number, point: VideoPoint) => void;
  frameUrl?: (t: number) => string;
  // Overlay mode: rendered on top of the player's seek bar (one timeline)
  // instead of drawing its own track and playhead.
  overlay?: boolean;
}

type Cluster = { x: number; points: VideoPoint[] };

const EventMarkerBar: React.FC<Props> = ({ points, durationMs, currentMs, idle = [], activeId, onHover, onSeek, frameUrl, overlay }) => {
  const barRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState<{ items: PopoverItem[]; x: number; y: number; below: boolean } | null>(null);

  // Measure once mounted and on resize so clustering uses real pixels.
  const measure = (el: HTMLDivElement | null) => {
    (barRef as any).current = el;
    if (el && el.clientWidth !== width) setWidth(el.clientWidth);
  };

  const total = Math.max(1, durationMs);
  const clusters = useMemo<Cluster[]>(() => {
    if (!width) return points.map((p) => ({ x: (p.tStartMs / total) * 100, points: [p] }));
    const sorted = [...points].sort((a, b) => a.tStartMs - b.tStartMs);
    const out: Cluster[] = [];
    for (const p of sorted) {
      const px = (p.tStartMs / total) * width;
      const last = out[out.length - 1];
      if (last && px - (last.x / 100) * width < CLUSTER_PX) last.points.push(p);
      else out.push({ x: (px / width) * 100, points: [p] });
    }
    return out;
  }, [points, total, width]);

  const showCard = (c: Cluster, el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const below = r.bottom + 260 < window.innerHeight;
    setHover({
      items: c.points.map((p) => ({
        id: p.id,
        severity: p.severity,
        label: p.title,
        corner: `${formatClock(p.tStartMs)}${p.tEndMs > p.tStartMs + 1000 ? ` – ${formatClock(p.tEndMs)}` : ""}`,
        quote: p.description,
        explanation: p.explanation,
        kind: p.kind,
        thumbnailUrl: frameUrl && p.frameTs.length ? frameUrl(p.frameTs[0]) : undefined,
      })),
      x: Math.min(Math.max(8, r.left - 180), window.innerWidth - 400),
      y: below ? r.bottom + 8 : r.top - 8,
      below,
    });
    onHover?.(c.points.map((p) => p.id));
  };
  const hideCard = () => {
    setHover(null);
    onHover?.([]);
  };

  return (
    <div className={overlay ? "absolute inset-0 pointer-events-none" : "px-3 pb-2.5"}>
      <div ref={measure} className={cn("relative h-6 select-none", overlay && "h-full")}>
        {/* Track with idle stretches dimmed (the seek bar is the track in overlay mode). */}
        {!overlay && <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-base-300" />}
        {idle.map((s, i) => (
          <div
            key={i}
            className={cn("absolute top-1/2 -translate-y-1/2 h-1.5", overlay ? "bg-base-content/10 dark:bg-base-content/15" : "bg-base-200 dark:bg-base-300/40")}
            style={{ left: `${(s.start_ms / total) * 100}%`, width: `${((s.end_ms - s.start_ms) / total) * 100}%` }}
            title="Nothing happening"
          />
        ))}
        {/* Range bars for longer points. */}
        {points
          .filter((p) => p.tEndMs - p.tStartMs > 3000)
          .map((p) => (
            <div
              key={`r-${p.id}`}
              className={cn("absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full opacity-40", kindStyle(p.kind).dot)}
              style={{ left: `${(p.tStartMs / total) * 100}%`, width: `${Math.max(0.3, ((p.tEndMs - p.tStartMs) / total) * 100)}%` }}
            />
          ))}
        {/* Playhead (the seek thumb is the playhead in overlay mode). */}
        {!overlay && <div className="absolute top-0 bottom-0 w-px bg-base-content/40" style={{ left: `${(Math.min(currentMs, total) / total) * 100}%` }} />}
        {/* Dots. */}
        {clusters.map((c, i) => {
          const top = c.points.reduce((a, p) => ((p.severity || 0) > (a.severity || 0) ? p : a), c.points[0]);
          const style = kindStyle(top.kind);
          const active = c.points.some((p) => p.id === activeId);
          const playing = c.points.some((p) => currentMs >= p.tStartMs && currentMs < Math.max(p.tEndMs, p.tStartMs + 1000));
          const dismissed = c.points.every((p) => p.status === "dismissed");
          return (
            <button
              key={i}
              type="button"
              data-tour="footage-marker"
              className={cn(
                "absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-base-100 transition-transform pointer-events-auto",
                style.dot,
                active || playing ? "size-4 ring-2 scale-110 z-10" : "size-3 hover:scale-125",
                (active || playing) && style.ring,
                playing && "animate-pulse",
                (top.severity || 0) >= 4 && !active && !playing && "ring-1 ring-offset-0 " + style.ring,
                dismissed && "opacity-40"
              )}
              style={{ left: `${c.x}%` }}
              onMouseEnter={(e) => showCard(c, e.currentTarget)}
              onMouseLeave={hideCard}
              onClick={() => onSeek(top.tStartMs, top)}
              title={`${top.title} at ${formatClock(top.tStartMs)}`}
            >
              {c.points.length > 1 && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[9px] font-semibold leading-none text-base-content/70 tabular-nums">{c.points.length}</span>
              )}
            </button>
          );
        })}
      </div>
      {hover && <LeadFlagPopover hover={{ leads: [], items: hover.items, x: hover.x, y: hover.y, below: hover.below }} />}
    </div>
  );
};

export default EventMarkerBar;
